import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const streamApi = axios.create({
  baseURL: API_URL,
  timeout: 10000, // 10 second timeout - fail fast instead of hanging
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth token from localStorage on every request (FIXED: parse userDetails JSON)
streamApi.interceptors.request.use((config) => {
  // 🔥 FIXED: Match AuthService.getToken() logic exactly
  const userDetailsString = localStorage.getItem('userDetails');
  let token = null;
  
  if (userDetailsString) {
    try {
      const userDetails = JSON.parse(userDetailsString);
      const expireDate = new Date(userDetails.expireDate);
      const now = new Date();
      
      if (now < expireDate && userDetails.token) {
        token = userDetails.token;
        console.log('[StreamService ✅ FIXED] Valid token found:', {
          userId: userDetails.user?.id || 'unknown',
          expiresIn: Math.round((expireDate - now) / 1000 / 60) + 'min',
          configUrl: config.url
        });
      } else {
        console.warn('[StreamService] Token expired or invalid:', {
          expired: now >= expireDate,
          hasToken: !!userDetails.token
        });
      }
    } catch (parseError) {
      console.error('[StreamService] Failed to parse userDetails:', parseError);
    }
  } else {
    console.warn('[StreamService] No userDetails in localStorage');
  }
  
  // Backward compatibility fallbacks
  if (!token) {
    token = localStorage.getItem('streamToken') || localStorage.getItem('token');
    if (token) {
      console.log('[StreamService] Using fallback token');
    }
  }
  
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
    console.log('[StreamService] → Authorization header added:', config.url);
  } else {
    console.warn('[StreamService ⚠️] No valid token - request will likely fail:', config.url);
  }
  
  return config;
});

// Handle 401 errors globally
streamApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('streamToken');
      localStorage.removeItem('token');
      localStorage.removeItem('streamUser');
      localStorage.removeItem('user');
      // Redirect to login
      window.location.href = '/login';
    }

    if (error.response?.status === 403) {
      // 🔥 ENHANCED DEBUGGING for token issues
      const userDetailsString = localStorage.getItem('userDetails');
      const hasToken = !!localStorage.getItem('streamToken') || !!localStorage.getItem('token') || !!userDetailsString;
      console.error('[StreamService 403 DEBUG] Token status:', {
        hasUserDetails: !!userDetailsString,
        hasStreamToken: !!localStorage.getItem('streamToken'),
        hasToken: !!localStorage.getItem('token'),
        backendError: error.response?.data,
        requestUrl: error.config?.url,
        requestHeaders: error.config?.headers
      });
      
      const code = error.response?.data?.code;
      if (code === 'NO_SUBSCRIPTION') {
        console.warn('[StreamService] Access denied: active subscription required');
      } else {
        console.error('[StreamService] 403 Forbidden:', error.response?.data || error.message);
      }
    }

    return Promise.reject(error);
  }
);

class StreamService {
  // Get all streams
  static async getAllStreams() {
    try {
      const response = await streamApi.get('/streams');
      return response.data;
    } catch (error) {
      console.error('Error fetching streams:', error);
      throw error;
    }
  }

  // Get stream statistics
  static async getStreamStats() {
    try {
      const response = await streamApi.get('/streams/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching stream stats:', error);
      throw error;
    }
  }

  // Get single stream
  static async getStream(id) {
    try {
      const response = await streamApi.get(`/streams/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stream:', error);
      throw error;
    }
  }

  // Create new stream
  static async createStream(streamData) {
    try {
      const response = await streamApi.post('/streams', streamData);
      return response.data;
    } catch (error) {
      console.error('Error creating stream:', error);
      // Return more detailed error information
      const errorDetails = error.response?.data?.details || error.response?.data?.error || error.message;
      throw new Error(errorDetails);
    }
  }

  // Update stream
  static async updateStream(id, updates) {
    try {
      const response = await streamApi.put(`/streams/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating stream:', error);
      throw error;
    }
  }

  // Delete stream
  static async deleteStream(id) {
    try {
      const response = await streamApi.delete(`/streams/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting stream:', error);
      throw error;
    }
  }

  // Get stream logs
  static async getStreamLogs(id, limit = 100) {
    try {
      const response = await streamApi.get(`/streams/${id}/logs`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching stream logs:', error);
      throw error;
    }
  }

  // Update stream status (for webhooks)
  static async updateStreamStatus(streamKey, status, metadata = {}) {
    try {
      const response = await streamApi.post(`/streams/status/${streamKey}`, {
        status,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error updating stream status:', error);
      throw error;
    }
  }

  // Health check
  static async healthCheck() {
    try {
      const response = await streamApi.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  // Regenerate stream key
  static async regenerateKey(id) {
    try {
      const response = await streamApi.post(`/streams/${id}/regenerate-key`);
      return response.data;
    } catch (error) {
      console.error('Error regenerating stream key:', error);
      throw error;
    }
  }

  // ---- Stream logo / watermark ----
  static async getLogo(id) {
    const r = await streamApi.get(`/streams/${id}/logo`);
    return r.data;
  }

  static async uploadLogo(id, file) {
    const fd = new FormData();
    fd.append('logo', file);
    const r = await streamApi.post(`/streams/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return r.data;
  }

  static async updateLogo(id, settings) {
    const r = await streamApi.put(`/streams/${id}/logo`, settings);
    return r.data;
  }

  static async removeLogo(id) {
    const r = await streamApi.delete(`/streams/${id}/logo`);
    return r.data;
  }

  // The preview route is auth-gated, so fetch it as a blob and hand back an object URL.
  static async fetchLogoObjectUrl(id) {
    try {
      const r = await streamApi.get(`/streams/${id}/logo/file`, { responseType: 'blob' });
      return URL.createObjectURL(r.data);
    } catch (_) {
      return null;
    }
  }

  // Stream mode controls
  static async getStreamMode() {
    try {
      const response = await streamApi.get('/vod/mode');
      return response.data;
    } catch (error) {
      console.error('Error getting stream mode:', error);
      throw error;
    }
  }

  static async enablePlaylistMode() {
    try {
      const response = await streamApi.post('/vod/mode/playlist');
      return response.data;
    } catch (error) {
      console.error('Error enabling playlist mode:', error);
      throw error;
    }
  }

  static async enableLiveMode() {
    try {
      const response = await streamApi.post('/vod/mode/live');
      return response.data;
    } catch (error) {
      console.error('Error enabling live mode:', error);
      throw error;
    }
  }

  // Backward-compatible wrappers
  static async startPlaylistMode() {
    return this.enablePlaylistMode();
  }

  static async stopPlaylistMode() {
    return this.enableLiveMode();
  }

  // Caption Management Methods

  // Get caption tracks for a stream
  static async getCaptions(streamId) {
    try {
      const response = await streamApi.get(`/streams/${streamId}/captions`);
      return response;
    } catch (error) {
      console.error('Error fetching captions:', error);
      throw error;
    }
  }

  // Add caption track to stream
  static async addCaption(streamId, captionData) {
    try {
      const response = await streamApi.post(`/streams/${streamId}/captions`, captionData);
      return response;
    } catch (error) {
      console.error('Error adding caption:', error);
      throw error;
    }
  }

  // Remove caption track from stream
  static async removeCaption(streamId, language) {
    try {
      const response = await streamApi.delete(`/streams/${streamId}/captions/${language}`);
      return response;
    } catch (error) {
      console.error('Error removing caption:', error);
      throw error;
    }
  }

  // Set default caption language
  static async setDefaultCaption(streamId, language) {
    try {
      const response = await streamApi.put(`/streams/${streamId}/captions/default`, { language });
      return response;
    } catch (error) {
      console.error('Error setting default caption:', error);
      throw error;
    }
  }
}

export default StreamService;
